import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  Play, Pause, Lock, GraduationCap, CheckCircle2, XCircle,
  Loader2, Award, Download, AlertTriangle, ChevronRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtTime(s) {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

/**
 * EmployeeCoursePlayer
 * --------------------
 * Full-screen, non-skippable training player + quiz + certificate flow.
 *
 *  Rules (Etapa 2):
 *   - Hidden native <video controls>.
 *   - User can only play/pause. NO scrubbing forward/backward.
 *   - playbackRate is fixed at 1x.
 *   - currentTime cannot exceed `maxWatchedRef.current`.
 *
 *  Rules (Etapa 3):
 *   - Quiz appears only after the `ended` event fires.
 *   - Score >= pass_threshold (70%) triggers automatic certificate
 *     issuance on the backend. The PDF is then offered for download.
 */
export default function EmployeeCoursePlayer({ open, courseKey, onClose }) {
  const { token } = useAuth();
  const [state, setState] = useState(null); // training-state from backend
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("intro"); // intro|video|quiz|result
  const videoRef = useRef(null);
  const maxWatchedRef = useRef(0);
  const lastSavedRef = useRef(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [seekBlockedFlash, setSeekBlockedFlash] = useState(false);

  // Quiz
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const loadState = useCallback(async () => {
    if (!open || !courseKey) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/employee/courses/${courseKey}/training`,
        { params: { token } },
      );
      setState(res.data);
      maxWatchedRef.current = Number(res.data.video_max_seconds || 0);
      lastSavedRef.current = maxWatchedRef.current;
      if (res.data.approved_at) {
        setPhase("result");
        setResult({
          approved: true,
          score_pct: res.data.best_score,
          certificate_id: res.data.certificate_id,
          already_approved: true,
        });
      } else if (res.data.video_completed_at) {
        setPhase("intro"); // user can jump straight to quiz from intro
      } else {
        setPhase("intro");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao abrir o curso");
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [open, courseKey, token, onClose]);

  useEffect(() => {
    if (!open) {
      // reset
      setState(null); setQuiz(null); setAnswers({}); setResult(null);
      setPhase("intro"); setCurrentTime(0); setDuration(0); setPlaying(false);
      maxWatchedRef.current = 0; lastSavedRef.current = 0;
      return;
    }
    loadState();
  }, [open, loadState]);

  // ===== Video lifecycle hooks ===============================================
  const saveProgress = useCallback(async (completed = false) => {
    if (!courseKey) return;
    try {
      const res = await axios.post(
        `${API}/employee/courses/${courseKey}/video-progress`,
        {
          seconds_watched: maxWatchedRef.current,
          completed,
        },
        { params: { token } },
      );
      lastSavedRef.current = maxWatchedRef.current;
      if (completed) {
        setState((s) => s
          ? { ...s, video_completed_at: new Date().toISOString() }
          : s);
      }
      return res.data;
    } catch (e) {
      // silent — will retry on next tick
    }
  }, [courseKey, token]);

  const onTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const t = v.currentTime;
    // Block forward seeking past the max watched + tiny epsilon.
    if (t > maxWatchedRef.current + 0.75) {
      v.currentTime = maxWatchedRef.current;
      setSeekBlockedFlash(true);
      setTimeout(() => setSeekBlockedFlash(false), 800);
      return;
    }
    if (t > maxWatchedRef.current) {
      maxWatchedRef.current = t;
    }
    setCurrentTime(t);
    // Persist every ~10s of new progress.
    if (maxWatchedRef.current - lastSavedRef.current >= 10) {
      saveProgress(false);
    }
  }, [saveProgress]);

  const onLoadedMetadata = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration || 0);
    // Always start fixed at the maximum already watched (resume).
    if (maxWatchedRef.current > 0 && maxWatchedRef.current < v.duration) {
      try { v.currentTime = maxWatchedRef.current; } catch (_e) { /* noop */ }
    }
  }, []);

  const onSeeking = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    // Browser/users can still trigger a seek through media session, keyboard
    // shortcuts on some platforms or external controllers. Force back.
    if (v.currentTime > maxWatchedRef.current + 0.75) {
      v.currentTime = maxWatchedRef.current;
      setSeekBlockedFlash(true);
      setTimeout(() => setSeekBlockedFlash(false), 800);
    }
  }, []);

  const onRateChange = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.playbackRate !== 1) v.playbackRate = 1;
  }, []);

  const onEnded = useCallback(async () => {
    setPlaying(false);
    maxWatchedRef.current = videoRef.current?.duration || maxWatchedRef.current;
    await saveProgress(true);
    toast.success("Vídeo concluído! Avaliação liberada.");
  }, [saveProgress]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  // Save progress on unmount/close.
  useEffect(() => {
    return () => {
      if (open && courseKey && maxWatchedRef.current > 0
        && maxWatchedRef.current !== lastSavedRef.current) {
        saveProgress(false);
      }
    };
  }, [open, courseKey, saveProgress]);

  // ===== Quiz fetch / submit =================================================
  const loadQuiz = async () => {
    try {
      const res = await axios.get(
        `${API}/employee/courses/${courseKey}/quiz`,
        { params: { token } },
      );
      setQuiz(res.data);
      setAnswers({});
      setPhase("quiz");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Quiz indisponível");
    }
  };

  const submitQuiz = async () => {
    if (!quiz) return;
    const unanswered = quiz.questions.filter((q) => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Responda todas as ${quiz.questions.length} perguntas`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/employee/courses/${courseKey}/quiz/submit`,
        { answers },
        { params: { token } },
      );
      setResult(res.data);
      setPhase("result");
      if (res.data.approved) {
        toast.success("Aprovado! Certificado emitido.");
      } else {
        toast.error(
          `Nota: ${res.data.score_pct}% (mínimo ${res.data.pass_threshold}%)`,
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar avaliação");
    } finally {
      setSubmitting(false);
    }
  };

  const retryQuiz = () => {
    setResult(null);
    setAnswers({});
    setPhase("intro");
    loadState();
  };

  const downloadCertificate = () => {
    const url = `${BACKEND_URL}/api/employee/courses/${courseKey}/certificate?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  // ===== Render ==============================================================
  if (!open) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[92vh] overflow-y-auto p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="course-player-dialog"
      >
        {/* Hidden accessible labels for Radix a11y (sr-only) */}
        <DialogTitle className="sr-only">
          Treinamento EAD — {state?.course_title || "AVI"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Player de vídeo bloqueado contra avanço seguido de avaliação
          e emissão automática de certificado digital ICP-Brasil.
        </DialogDescription>
        {loading || !state ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 text-white p-4 sm:p-5 flex items-center gap-3">
              <div className="bg-white/15 rounded-lg p-2">
                <GraduationCap className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-indigo-200">
                  Treinamento EAD
                </div>
                <div className="text-base sm:text-lg font-bold leading-tight">
                  {state.course_title}
                </div>
                <div className="text-[12px] text-indigo-200 mt-0.5">
                  {state.course_tema} • {state.carga_horaria}
                </div>
              </div>
              <button
                className="text-white/80 hover:text-white text-xs underline"
                onClick={() => onClose?.()}
                data-testid="course-player-close-btn"
              >
                Sair
              </button>
            </div>

            <div className="p-4 sm:p-6">
              <Stepper phase={phase} approved={state.approved_at || result?.approved} />

              <AnimatePresence mode="wait">
                {phase === "intro" && (
                  <motion.div
                    key="intro"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-5 space-y-4"
                  >
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900">
                      <AlertTriangle className="inline w-4 h-4 mr-1" />
                      <strong>Regras do treinamento:</strong> o vídeo
                      é obrigatório. Você poderá apenas <strong>pausar</strong>{" "}
                      e <strong>continuar</strong>. <strong>Não é
                      possível avançar, pular ou acelerar</strong> a aula.
                      Após assistir até o final, a avaliação será liberada.
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 text-sm">
                      <InfoBox label="Vídeo da aula"
                        value={state.video_completed_at ? "Concluído" : "Pendente"}
                        ok={!!state.video_completed_at} />
                      <InfoBox label="Avaliação"
                        value={`${state.total_questions} questões`}
                        sub={`Aprovação ≥ ${state.pass_threshold}%`} />
                      <InfoBox label="Certificado"
                        value={state.approved_at ? "Emitido" : "Após aprovação"}
                        ok={!!state.approved_at} />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-2">
                      {!state.video_completed_at && (
                        <Button
                          className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          onClick={() => setPhase("video")}
                          data-testid="course-start-video-btn"
                        >
                          <Play className="w-4 h-4 mr-1.5" />
                          {maxWatchedRef.current > 0
                            ? "Continuar de onde parei"
                            : "Iniciar aula"}
                        </Button>
                      )}
                      {state.video_completed_at && !state.approved_at && (
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={loadQuiz}
                          data-testid="course-start-quiz-btn"
                        >
                          <ChevronRight className="w-4 h-4 mr-1.5" />
                          Iniciar avaliação
                        </Button>
                      )}
                      {state.approved_at && (
                        <Button
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={downloadCertificate}
                          data-testid="course-download-cert-btn"
                        >
                          <Download className="w-4 h-4 mr-1.5" />
                          Baixar certificado
                        </Button>
                      )}
                    </div>
                  </motion.div>
                )}

                {phase === "video" && (
                  <motion.div
                    key="video"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="mt-5"
                  >
                    <div className="relative bg-black rounded-md overflow-hidden">
                      <video
                        ref={videoRef}
                        className="w-full aspect-video bg-black"
                        src={`${BACKEND_URL}${state.video_url}`}
                        playsInline
                        preload="metadata"
                        onTimeUpdate={onTimeUpdate}
                        onLoadedMetadata={onLoadedMetadata}
                        onSeeking={onSeeking}
                        onRateChange={onRateChange}
                        onEnded={onEnded}
                        onPlay={() => setPlaying(true)}
                        onPause={() => setPlaying(false)}
                        onContextMenu={(e) => e.preventDefault()}
                        controlsList="nodownload noplaybackrate nofullscreen"
                        disablePictureInPicture
                        data-testid="course-player-video"
                      />
                      <AnimatePresence>
                        {seekBlockedFlash && (
                          <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="absolute inset-0 flex items-center justify-center bg-black/60"
                          >
                            <div className="bg-red-600/95 text-white px-4 py-2 rounded-md flex items-center gap-2 shadow-lg">
                              <Lock className="w-4 h-4" />
                              <span className="text-sm font-semibold">
                                Avanço bloqueado — você não pode pular a aula
                              </span>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <CustomVideoControls
                      playing={playing}
                      togglePlay={togglePlay}
                      currentTime={currentTime}
                      maxWatched={maxWatchedRef.current}
                      duration={duration}
                    />
                    <div className="mt-3 text-[12px] text-slate-500 flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" />
                      Apenas Play/Pause. Velocidade fixa em 1x.
                    </div>
                  </motion.div>
                )}

                {phase === "quiz" && quiz && (
                  <motion.div
                    key="quiz"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-5 space-y-5"
                  >
                    <div className="text-sm text-slate-600">
                      Responda todas as <strong>{quiz.total_questions}</strong>{" "}
                      questões. Aprovação a partir de{" "}
                      <strong>{quiz.pass_threshold}%</strong>.
                    </div>
                    {quiz.questions.map((q, idx) => (
                      <div key={q.id}
                        className="rounded-lg border border-slate-200 p-3 sm:p-4"
                        data-testid={`quiz-q-${q.id}`}
                      >
                        <div className="text-sm font-semibold text-slate-900 mb-2">
                          {idx + 1}. {q.text}
                        </div>
                        <div className="space-y-1.5">
                          {q.options.map((opt) => {
                            const selected = answers[q.id] === opt.key;
                            return (
                              <label
                                key={opt.key}
                                className={`flex items-start gap-2 p-2 rounded-md cursor-pointer border transition ${
                                  selected
                                    ? "border-indigo-500 bg-indigo-50"
                                    : "border-slate-200 hover:bg-slate-50"
                                }`}
                                data-testid={`quiz-option-${q.id}-${opt.key}`}
                              >
                                <input
                                  type="radio"
                                  name={q.id}
                                  className="mt-1"
                                  checked={selected}
                                  onChange={() =>
                                    setAnswers((p) => ({ ...p, [q.id]: opt.key }))
                                  }
                                />
                                <span className="text-sm text-slate-800">
                                  <strong className="text-indigo-700 mr-1">
                                    {opt.key})
                                  </strong>
                                  {opt.text}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="ghost" onClick={() => setPhase("intro")}>
                        Voltar
                      </Button>
                      <Button
                        onClick={submitQuiz}
                        disabled={submitting}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="course-submit-quiz-btn"
                      >
                        {submitting
                          ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                          : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                        Enviar avaliação
                      </Button>
                    </div>
                  </motion.div>
                )}

                {phase === "result" && result && (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mt-5"
                  >
                    {result.approved ? (
                      <div className="rounded-lg border-2 border-emerald-500 bg-emerald-50 p-5 text-center">
                        <Award className="w-12 h-12 mx-auto text-emerald-600 mb-2" />
                        <div className="text-lg font-bold text-emerald-900">
                          Aprovado!
                        </div>
                        <div className="text-sm text-emerald-800 mt-1">
                          Sua nota: <strong>{result.score_pct}%</strong>
                          {" "}— certificado emitido com assinatura digital
                          ICP-Brasil.
                        </div>
                        <Button
                          onClick={downloadCertificate}
                          className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="course-result-download-btn"
                        >
                          <Download className="w-4 h-4 mr-1.5" />
                          Baixar certificado
                        </Button>
                      </div>
                    ) : (
                      <div className="rounded-lg border-2 border-red-400 bg-red-50 p-5 text-center">
                        <XCircle className="w-12 h-12 mx-auto text-red-500 mb-2" />
                        <div className="text-lg font-bold text-red-900">
                          Reprovado
                        </div>
                        <div className="text-sm text-red-800 mt-1">
                          Sua nota: <strong>{result.score_pct}%</strong> —
                          mínimo de {result.pass_threshold}% exigido.
                        </div>
                        <div className="flex justify-center gap-2 mt-4">
                          <Button variant="ghost" onClick={() => onClose?.()}>
                            Fechar
                          </Button>
                          <Button
                            onClick={retryQuiz}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="course-result-retry-btn"
                          >
                            Tentar novamente
                          </Button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stepper({ phase, approved }) {
  const items = [
    { key: "video", label: "Aula" },
    { key: "quiz", label: "Avaliação" },
    { key: "result", label: "Certificado" },
  ];
  const order = { intro: 0, video: 0, quiz: 1, result: 2 };
  const cur = approved ? 2 : (order[phase] ?? 0);
  return (
    <div className="flex items-center gap-2 text-xs">
      {items.map((it, i) => (
        <div key={it.key} className="flex items-center gap-2">
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold ${
              i < cur
                ? "bg-emerald-500 text-white"
                : i === cur
                ? "bg-indigo-600 text-white"
                : "bg-slate-200 text-slate-500"
            }`}
          >
            {i < cur ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
          </div>
          <span
            className={`text-[12px] ${
              i <= cur ? "text-slate-900 font-semibold" : "text-slate-400"
            }`}
          >
            {it.label}
          </span>
          {i < items.length - 1 && (
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
          )}
        </div>
      ))}
    </div>
  );
}

function InfoBox({ label, value, sub, ok }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className={`text-sm font-bold ${
        ok ? "text-emerald-700" : "text-slate-900"
      }`}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function CustomVideoControls({ playing, togglePlay, currentTime, maxWatched, duration }) {
  const watchedPct = duration > 0 ? (maxWatched / duration) * 100 : 0;
  const playPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  return (
    <div className="mt-2 flex items-center gap-3">
      <button
        onClick={togglePlay}
        className="w-10 h-10 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center transition"
        data-testid="course-player-playpause-btn"
        type="button"
      >
        {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
      </button>
      <div className="flex-1">
        <div className="relative h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-slate-300/80"
            style={{ width: `${watchedPct}%` }}
            title="Trecho liberado"
          />
          <div
            className="absolute inset-y-0 left-0 bg-indigo-600"
            style={{ width: `${playPct}%` }}
            title="Posição atual"
          />
        </div>
        <div className="flex justify-between text-[11px] text-slate-500 mt-1 font-mono">
          <span>{fmtTime(currentTime)}</span>
          <span>{fmtTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}
